import { CustomTab } from "@/components/UI/Tabs";
import { formatDate, getStatusColor } from "@/utils/helper";
import { Button, Col, Modal, Row, Typography } from "antd";
import { useState, useMemo } from "react";
import { ClockCircleOutlined } from "@ant-design/icons";
import {
  useGetFulfillmentByReferenceQuery,
  useGetOrderByReferenceQuery,
} from "@/services/api/orders/ordersApi";
import FulfillmentProcess from "../../component/SingleOrdalFufillment";

const { Title } = Typography;

const EventOrderDetailsModal = ({ open, onClose, orderData }) => {
  const {
    data: orderDetailsByRef,
    isLoading: orderDetailsLoading,
    isFetching,
  } = useGetOrderByReferenceQuery(orderData?.orderId, { skip: !open });

  const { data: fufillmentDetailsByRef } = useGetFulfillmentByReferenceQuery(
    orderData?.orderId,
    { skip: !open }
  );

  // console.log("Fetched event order details by reference:", orderDetailsByRef);
  const currentStatusId = fufillmentDetailsByRef?.current_status_id || 1;
  const orderDetails = orderDetailsByRef?.[0] || null;
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [{ label: "Order Summary" }, { label: "Order Fufillment" }];

  // Map API data to catering details with fallback values
  const cateringDetails = useMemo(() => {
    const eventData = orderDetails?.items?.[0]?.productable || {};

    // Helper to safely get .name from object or fallback
    const getName = (obj) =>
      obj ? obj.name || obj.title || "Not specified" : "Not specified";
    const getNames = (arr) =>
      Array.isArray(arr)
        ? arr.map(getName).filter(Boolean).join(", ")
        : "Not specified";

    return [
      {
        question: "When is the event?",
        answer: eventData.event_date
          ? formatDate(eventData.event_date)
          : eventData.start_date
          ? formatDate(eventData.start_date)
          : "Date not specified",
      },
      {
        question: "What type of event are you planning?",
        answer: getName(eventData.event_type),
      },
      {
        question: "How many guests are you expecting?",
        answer: eventData.expected_guests?.toString() || "Not specified",
      },
      // {
      //   question: "How would you want us to provide the catering service?",
      //   answer:
      //     getName(eventData.service_type) || deliveryMode || "Not specified",
      // },
      {
        question: "Venue / Delivery Location / Pickup location",
        answer:
          eventData.venue ||
          eventData.delivery_address ||
          orderDetails?.delivery_address ||
          eventData.pickup_address ||
          "Location not specified",
      },
      {
        question: "Dietary Requirements",
        answer: Array.isArray(eventData.dietary_preferences)
          ? getNames(eventData.dietary_preferences)
          : getName(eventData.dietary_preferences),
      },
      {
        question: "Drinks",
        answer: Array.isArray(eventData.beverage_types)
          ? getNames(eventData.beverage_types)
          : getName(eventData.beverage_types),
      },
      {
        question: "Package type",
        answer: Array.isArray(eventData.package_types)
          ? getNames(eventData.package_types)
          : getName(eventData.package_types),
      },
      {
        question: "Customer's Role",
        answer: eventData.role || "Not specified",
      },
      {
        question: "Customer's Contact",
        answer:
          eventData.phone_number ||
          orderDetails?.user?.phone ||
          "Not specified",
      },
      {
        question: "Budget (per person)",
        answer: eventData.budget_per_person
          ? `₦${parseFloat(eventData.budget_per_person).toLocaleString()}`
          : "Not specified",
      },
      {
        question: "Total Budget",
        answer: eventData.total_budget
          ? `₦${parseFloat(eventData.total_budget).toLocaleString()}`
          : "Not specified",
      },
      {
        question: "Food Request",
        answer: eventData.food_request || "Not Specified",
      },
      {
        question: "Quoted Amount",
        answer: eventData.quoted_amount
          ? `₦${parseFloat(eventData.quoted_amount).toLocaleString()}`
          : "Not specified",
      },
    ];
  }, [orderDetails]); // Don't forget dependencies!

  const renderTabContent = () => {
    if (activeTab === 0) {
      return (
        <div className="mt-6">
          {orderDetailsLoading || isFetching ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading order details...</div>
            </div>
          ) : (
            <>
              <Row gutter={[24, 24]}>
                {cateringDetails.map((detail, index) => (
                  <Col xs={24} md={12} key={index}>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        {detail.question}
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-600">
                        {detail.answer}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              <Row gutter={[24, 24]} className="mt-6">
                <Col xs={24}>
                  <div className="space-y-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Quote Document
                    </div>
                    {orderDetails?.items?.[0]?.productable?.quote_url ? (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <a
                          href={orderDetails?.items?.[0]?.productable.quote_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          View Quote PDF
                        </a>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">
                        No quote document available
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      );
    }

    return (
      <FulfillmentProcess
        orderId={orderData?.originalData?.id}
        orderReference={orderData?.orderId}
        currentStatusId={currentStatusId}
        fulfillmentStages={fufillmentDetailsByRef?.fulfilment_stages || []}
        onSuccess={() => {
          console.log("Order status updated successfully");
        }}
        onError={(error) => {
          console.error("Failed to update order:", error);
        }}
      />
    );
  };

  // Get customer name with fallback
  const customerName = useMemo(() => {
    if (!orderDetails?.user) return "N/A";

    const { first_name, last_name } = orderDetails.user;
    if (first_name && last_name) {
      return `${first_name} ${last_name}`;
    }
    return first_name || last_name || "N/A";
  }, [orderDetails]);

  // Get customer contact details
  const customerContact = useMemo(() => {
    if (!orderDetails?.user) return [];

    const contact = [];
    if (orderDetails.user.email) contact.push(orderDetails.user.email);
    if (orderDetails.user.phone) contact.push(orderDetails.user.phone);

    return contact;
  }, [orderDetails]);

  // Calculate estimated time to complete
  const estimatedTime = useMemo(() => {
    if (!orderDetails?.estimated_end_date || !orderDetails?.start_date) {
      return "Not calculated";
    }

    try {
      const start = new Date(orderDetails.start_date);
      const end = new Date(orderDetails.estimated_end_date);
      const diffMs = end - start;

      if (diffMs <= 0) return "Overdue";

      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffDays > 0) {
        return `${diffDays}d ${diffHours}h`;
      } else if (diffHours > 0) {
        return `${diffHours}h ${diffMins}min`;
      } else {
        return `${diffMins}min`;
      }
    } catch (error) {
      return "Not calculated";
    }
  }, [orderDetails]);

  const { color, backgroundColor } = getStatusColor(
    orderDetails?.status?.name || orderData?.status || "Incoming Request"
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="space-y-1">
          <Title level={4} className="mb-2">
            Order Details{" "}
            <span
              className="px-3 py-1 rounded-lg text-sm ml-2"
              style={{ color, backgroundColor }}
            >
              {orderDetails?.status?.name ||
                orderData?.status ||
                "Incoming Request"}
            </span>
          </Title>
          <p className="text-sm text-gray-400 m-0">
            See details of customer's catering request
          </p>
        </div>
      }
      maskClosable
      destroyOnClose
      width="90vw"
      style={{ top: 10, maxWidth: 1400 }}
      styles={{ body: { maxHeight: "calc(100vh - 190px)", overflowY: "auto" } }}
      footer={
        <div className="flex justify-end items-center">
          <Button onClick={onClose} size="large">
            Cancel
          </Button>
        </div>
      }
    >
      <div className="min-h-[70vh] p-6">
        <Row gutter={[24, 24]} className="my-4">
          <Col xs={24} lg={5}>
            <div className="py-4 border-b border-gray-200">
              <Title level={3} className="m-0 mb-2">
                #{orderDetails?.reference || orderData?.orderId || "Loading..."}
              </Title>
              <div className="text-sm text-gray-400">
                Last update:{" "}
                {orderDetails?.updated_at
                  ? formatDate(orderDetails.updated_at)
                  : orderDetails?.last_order_date
                  ? formatDate(orderDetails.last_order_date)
                  : "N/A"}
              </div>
            </div>

            <div className="mt-6 space-y-6 gap-6 flex lg:block">
              {[
                {
                  label: "CUSTOMER",
                  value: customerName,
                  extra: customerContact,
                },
                {
                  label: "ORDER PLACED",
                  value: orderDetails?.start_date
                    ? formatDate(orderDetails.start_date)
                    : orderDetails?.created_at
                    ? formatDate(orderDetails.created_at)
                    : "N/A",
                },
                {
                  label: "DUE BY",
                  value: orderDetails?.estimated_end_date
                    ? formatDate(orderDetails.estimated_end_date)
                    : "N/A",
                },
                {
                  label: "ESTIMATED TIME TO COMPLETE ORDER",
                  icon: <ClockCircleOutlined className="text-gray-400 mt-1" />,
                  value: estimatedTime,
                  isEstimate: true,
                },
              ].map((item, i) => (
                <div key={i}>
                  <div className="text-xs text-gray-500 mb-2">{item.label}</div>
                  {item.isEstimate ? (
                    <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2">
                      {item.icon}
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ) : (
                    <div className={item.icon ? "flex items-start gap-2" : ""}>
                      {item.icon}
                      <div>
                        <div className="font-medium">{item.value}</div>
                        {item.extra?.length > 0 &&
                          item.extra.map((text, j) => (
                            <div key={j} className="text-sm text-gray-500 mt-1">
                              {text}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Col>

          <Col
            xs={24}
            lg={19}
            className="p-8 border border-gray-200 rounded-lg bg-white"
          >
            <CustomTab
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            {renderTabContent()}
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default EventOrderDetailsModal;
